import React from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { SECTION_TITLE_SIZE } from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { createChangeSettings } from '../utils/createChangeSettings';
import { ShopThisLookIcon } from '../../Icons/DropDownIcons/ShopThisLook';
import { WidgetView } from 'common';
import { ShopThisLookSelector } from '../selectors/ShopThisLookSelector';
import { SettingsCheckbox } from '../subcomponents/SettingsCheckbox';
import { Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';

const QuickShopThisLook = ({ opened, onOpen, editedWidget, setEditedWidget }: SectionProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  const handleShopThisLookChange = <K extends keyof WidgetView['settings']>(
    key: K,
    value: WidgetView['settings'][K],
  ) => {
    if (value === editedWidget.settings[key]) {
      return;
    }
    
    const isComingFromNone = editedWidget.settings.shop_this_look_display === 'none';
    const isGoingToNone = value === 'none';
    const isEnablingSTL = !isGoingToNone && (value === 'modal' || value === 'product_list');
    
    const newSettings: Partial<WidgetView['settings']> = {
      [key]: value, 
      // Set product settings to false when shop_this_look_display is 'none'
      // Enable both products only when switching FROM 'none' TO 'modal' or 'product_list'
      // Otherwise preserve current state when switching between modal and product_list
      has_exact_products: isGoingToNone ? false : (isEnablingSTL && isComingFromNone) ? true : editedWidget.settings.has_exact_products,
      has_similar_products: isGoingToNone ? false : (isEnablingSTL && isComingFromNone) ? true : editedWidget.settings.has_similar_products,
      display_exact_products_only: isGoingToNone ? false : editedWidget.settings.display_exact_products_only,
      display_similar_products_only: isGoingToNone ? false : editedWidget.settings.display_similar_products_only,
      // Set shop_this_look boolean value to support v5 and v6 widgets
      shop_this_look: !isGoingToNone,
    };

    changeSettings(newSettings);
  };

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <ShopThisLookIcon />
          {lang.title.main}
        </SectionTitle>
      }
      opened={opened}
      afterTitle={
        <Tooltip title={lang.tooltips.shopThisLook} placement="top">
          <span className={infoShopThisLookClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
      onOpen={onOpen}
    >
      <SectionContent>
        <ShopThisLookSelector
          shop_this_look_display={editedWidget.settings.shop_this_look_display}
          onChange={(value) => handleShopThisLookChange('shop_this_look_display', value)}
          afterProductList={
            editedWidget.settings.shop_this_look_display === 'product_list' && (
              <SettingsCheckbox
                label={lang.fields.displayDesktopPlayer}
                checked={editedWidget.settings.display_desktop_player}
                onClick={() => changeSettings({ display_desktop_player: !editedWidget.settings.display_desktop_player })}
              />
            )
          }
        />
      </SectionContent>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    title: {
      main: 'Shop This Look',
    },
    tooltips: {
      shopThisLook: 'Configure how product recommendations are displayed when users interact with your UGCs',
    },
    fields: {
      displayDesktopPlayer: 'Display desktop player',
    },
  },
});

const infoShopThisLookClass = style({
  marginLeft: 10,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

export default QuickShopThisLook;
